import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ethers } from 'ethers';
import '../styles/RankAchievements.css';

const RankAchievements = ({ stakingContract, userInfo, refreshData }) => {
  const [loading, setLoading] = useState(false);
  const [achievementData, setAchievementData] = useState({
    currentLevel: 0,
    nextLevel: 1,
    progress: 0
  });

  // Updated achievement levels with CORRECTED requirements
  const ACHIEVEMENT_LEVELS = useMemo(() => ({
    0: { 
      name: "UNRANKED", 
      color: "#94a3b8", 
      requirement: "No achievements yet", 
      icon: "📦", 
      teamVolume: 0, 
      directRefs: 0,
      details: "Start your journey by making your first investment"
    },
    1: { 
      name: "Cluster", 
      color: "#8B4513", 
      requirement: "3 direct referrals & $3,000 team volume", 
      icon: "🌟", 
      teamVolume: 3000, 
      directRefs: 3,
      details: "Build a small team of direct referrals"
    },
    2: { 
      name: "Bronze", 
      color: "#CD7F32", 
      requirement: "3 Cluster + 3 direct & $90,000 team volume", 
      icon: "🥉", 
      teamVolume: 90000, 
      directRefs: 3,
      downlineReq: { cluster: 3 },
      details: "Help 3 people achieve Cluster rank"
    },
    3: { 
      name: "Silver", 
      color: "#C0C0C0", 
      requirement: "3 Bronze & $2,700,000 team volume", 
      icon: "🥈", 
      teamVolume: 2700000, 
      directRefs: 0,
      downlineReq: { bronze: 3 },
      details: "Lead 3 Bronze achievers"
    },
    4: { 
      name: "Gold", 
      color: "#FFD700", 
      requirement: "3 Silver & $8,100,000 team volume", 
      icon: "🥇", 
      teamVolume: 8100000, 
      directRefs: 0,
      downlineReq: { silver: 3 },
      details: "Develop 3 Silver leaders"
    },
    5: { 
      name: "Platinum", 
      color: "#E5E4E2", 
      requirement: "3 Gold & $24,300,000 team volume", 
      icon: "💎", 
      teamVolume: 24300000, 
      directRefs: 0,
      downlineReq: { gold: 3 },
      details: "Mentor 3 Gold achievers"
    },
    6: { 
      name: "Diamond", 
      color: "#B9F2FF", 
      requirement: "3 Platinum & $72,900,000 team volume", 
      icon: "💍", 
      teamVolume: 72900000, 
      directRefs: 0,
      downlineReq: { platinum: 3 },
      details: "Guide 3 Platinum leaders"
    },
    7: { 
      name: "Double Diamond", 
      color: "#4169E1", 
      requirement: "3 Diamond & $145,800,000 team volume", 
      icon: "💎", 
      teamVolume: 145800000, 
      directRefs: 0,
      downlineReq: { diamond: 3 },
      details: "Create 3 Diamond achievers"
    },
    8: { 
      name: "Triple Diamond", 
      color: "#9400D3", 
      requirement: "3 Double Diamond & $218,700,000 team volume", 
      icon: "💎", 
      teamVolume: 218700000, 
      directRefs: 0,
      downlineReq: { doubleDiamond: 3 },
      details: "Develop 3 Double Diamond leaders"
    },
    9: { 
      name: "Crown", 
      color: "#FF6347", 
      requirement: "3 Triple Diamond & $656,100,000 team volume", 
      icon: "👑", 
      teamVolume: 656100000, 
      directRefs: 0,
      downlineReq: { tripleDiamond: 3 },
      details: "Ultimate achievement - lead 3 Triple Diamonds"
    }
  }), []);

  // Helper function to convert BigNumber to regular number
  const convertTeamVolume = useCallback((teamVolumeWei) => {
    if (!teamVolumeWei) return 0;
    
    try {
      // Handle different data types
      let valueToConvert = teamVolumeWei;
      
      // If it's already a number or string number, return it directly
      if (typeof teamVolumeWei === 'number') {
        return teamVolumeWei;
      }
      
      if (typeof teamVolumeWei === 'string') {
        // If it's a decimal string like "0.0", parse it directly
        if (teamVolumeWei.includes('.')) {
          return parseFloat(teamVolumeWei);
        }
        // If it's a whole number string, try to parse as BigNumber
        if (teamVolumeWei === '0' || teamVolumeWei === '') {
          return 0;
        }
      }
      
      // If it's a BigNumber object, convert from wei to ether
      if (teamVolumeWei._isBigNumber || ethers.BigNumber.isBigNumber(teamVolumeWei)) {
        return parseFloat(ethers.utils.formatEther(teamVolumeWei));
      }
      
      // Try to create BigNumber from string and convert
      const bigNumberValue = ethers.BigNumber.from(teamVolumeWei.toString());
      return parseFloat(ethers.utils.formatEther(bigNumberValue));
      
    } catch (error) {
      console.error('Error converting team volume:', error, 'Value:', teamVolumeWei);
      // If all else fails, try to parse as float
      const floatValue = parseFloat(teamVolumeWei);
      return isNaN(floatValue) ? 0 : floatValue;
    }
  }, []);

  const loadAchievementData = useCallback(async () => {
    if (!stakingContract || !userInfo?.id) return;

    setLoading(true);
    try {
      // Get current achievement level from contract
      const achievementLevel = await stakingContract.getAchievementLevel(userInfo.id);
      const currentLevel = parseInt(achievementLevel.toString());

      // Calculate progress to next level
      const currentTeamVolume = convertTeamVolume(userInfo.teamVolume);
      const currentDirectRefs = parseInt(userInfo.directReferrals || 0);
      const nextLevel = Math.min(currentLevel + 1, 9);
      
      let progress = 0;
      if (nextLevel <= 9 && currentLevel < 9) {
        const nextLevelReq = ACHIEVEMENT_LEVELS[nextLevel];
        if (nextLevelReq) {
          // Calculate volume progress
          const volumeProgress = (currentTeamVolume / nextLevelReq.teamVolume) * 100;
          
          // Calculate direct referral progress
          const directRefProgress = nextLevelReq.directRefs > 0 ? 
            (currentDirectRefs / nextLevelReq.directRefs) * 100 : 100;
          
          // Use the minimum of both requirements
          progress = Math.min(volumeProgress, directRefProgress);
        }
      }

      setAchievementData({
        currentLevel: currentLevel,
        nextLevel,
        progress: Math.min(progress, 100)
      });

    } catch (error) {
      console.error('Failed to load achievement data:', error);
      setAchievementData({
        currentLevel: 0,
        nextLevel: 1,
        progress: 0
      });
    } finally {
      setLoading(false);
    }
  }, [stakingContract, userInfo, ACHIEVEMENT_LEVELS, convertTeamVolume]);

  useEffect(() => {
    loadAchievementData();
  }, [loadAchievementData]);

  const formatNumber = useCallback((num, decimals = 2) => {
    if (isNaN(num)) return '0';
    return parseFloat(num).toLocaleString('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    });
  }, []);

  const formatVolume = useCallback((volume) => {
    const num = parseFloat(volume);
    if (num >= 1000000) {
      return `$${(num / 1000000).toFixed(2)}M`;
    } else if (num >= 1000) {
      return `$${(num / 1000).toFixed(1)}K`;
    }
    return `$${num.toFixed(2)}`;
  }, []);

  const getCurrentLevelData = useCallback(() => {
    return ACHIEVEMENT_LEVELS[achievementData.currentLevel] || ACHIEVEMENT_LEVELS[0];
  }, [ACHIEVEMENT_LEVELS, achievementData.currentLevel]);

  const getNextLevelData = useCallback(() => {
    return ACHIEVEMENT_LEVELS[achievementData.nextLevel] || null;
  }, [ACHIEVEMENT_LEVELS, achievementData.nextLevel]);

  const getProgressRequirements = useCallback(() => {
    const nextLevel = getNextLevelData();
    if (!nextLevel || !userInfo || achievementData.currentLevel >= 9) return null;

    const currentTeamVolume = convertTeamVolume(userInfo.teamVolume);
    const currentDirectRefs = parseInt(userInfo.directReferrals || 0);

    const volumeNeeded = Math.max(0, nextLevel.teamVolume - currentTeamVolume);
    const directRefsNeeded = Math.max(0, nextLevel.directRefs - currentDirectRefs);

    return {
      volumeNeeded,
      directRefsNeeded,
      volumeProgress: Math.min((currentTeamVolume / nextLevel.teamVolume) * 100, 100),
      directRefProgress: nextLevel.directRefs > 0 ? 
        Math.min((currentDirectRefs / nextLevel.directRefs) * 100, 100) : 100,
      downlineReq: nextLevel.downlineReq || null
    };
  }, [getNextLevelData, userInfo, achievementData.currentLevel, convertTeamVolume]);

  if (loading) {
    return (
      <div className="trail-rank-dashboard">
        <div className="trail-rank-loading-container">
          <div className="trail-rank-ai-loading-spinner"></div>
          <p>Loading achievement data...</p>
        </div>
      </div>
    );
  }

  const currentLevelData = getCurrentLevelData();
  const nextLevelData = getNextLevelData();
  const progressReqs = getProgressRequirements();
  const currentTeamVolume = convertTeamVolume(userInfo?.teamVolume);

  return (
    <div className="trail-rank-dashboard">
      {/* Header */}
      <div className="trail-rank-header">
        <div className="trail-rank-title-section">
          <div className="trail-rank-title-icon">🏆</div>
          <div>
            <h1 className="trail-rank-title">Achievements & Rankings</h1>
            <p className="trail-rank-subtitle">Build your team and climb through the achievement levels</p>
          </div>
        </div>
      </div>

      {/* Current Achievement Status */}
      <div className="trail-current-achievement-card">
        <div className="trail-achievement-display">
          <div className="trail-current-badge" style={{ backgroundColor: currentLevelData.color }}>
            <div className="trail-badge-icon">{currentLevelData.icon}</div>
            <div className="trail-badge-name">{currentLevelData.name}</div>
          </div>
          
          <div className="trail-achievement-info">
            <h3>Current Achievement Level</h3>
            <p className="trail-achievement-description">{currentLevelData.requirement}</p>
            <p className="trail-achievement-details">{currentLevelData.details}</p>
            
            <div className="trail-current-stats">
              <div className="trail-current-stat">
                <span className="trail-stat-label">Team Volume:</span>
                <span className="trail-stat-value">{formatVolume(currentTeamVolume)}</span>
              </div>
              <div className="trail-current-stat">
                <span className="trail-stat-label">Direct Referrals:</span>
                <span className="trail-stat-value">{userInfo?.directReferrals || 0}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Progress to Next Level */}
        {nextLevelData && progressReqs && achievementData.currentLevel < 9 && (
          <div className="trail-progress-section">
            <div className="trail-progress-header">
              <h4>Progress to {nextLevelData.name}</h4>
              <div className="trail-progress-percentage">{progressReqs.volumeProgress.toFixed(1)}%</div>
            </div>
            
            <div className="trail-progress-bar">
              <div 
                className="trail-progress-fill" 
                style={{ width: `${Math.min(progressReqs.volumeProgress, 100)}%` }}
              ></div>
            </div>
            
            <div className="trail-progress-requirements">
              <div className="trail-requirement-item">
                <span className="trail-req-label">Team Volume Required:</span>
                <span className="trail-req-value">{formatVolume(nextLevelData.teamVolume)}</span>
                <span className="trail-req-remaining">
                  ({formatVolume(progressReqs.volumeNeeded)} remaining)
                </span>
              </div>
              
              {nextLevelData.directRefs > 0 && (
                <div className="trail-requirement-item">
                  <span className="trail-req-label">Direct Referrals Required:</span>
                  <span className="trail-req-value">{nextLevelData.directRefs}</span>
                  <span className="trail-req-remaining">
                    ({progressReqs.directRefsNeeded} remaining)
                  </span>
                </div>
              )}

              {progressReqs.downlineReq && (
                <div className="trail-requirement-item downline-requirement">
                  <span className="trail-req-label">Team Achievement Required:</span>
                  <span className="trail-req-value">
                    {Object.entries(progressReqs.downlineReq).map(([rank, count]) => 
                      `${count} ${rank.charAt(0).toUpperCase() + rank.slice(1)} achievers`
                    ).join(', ')}
                  </span>
                  <span className="trail-req-remaining">
                    (Help your team members achieve these ranks)
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {achievementData.currentLevel >= 9 && (
          <div className="trail-max-level-badge">
            <div className="trail-crown-icon">👑</div>
            <div>
              <h4>Crown Achievement Unlocked!</h4>
              <p>You've reached the highest level in the Trail AI ecosystem</p>
            </div>
          </div>
        )}
      </div>

      {/* Achievement Levels Overview */}
      <div className="trail-achievements-grid">
        <div className="trail-achievements-card">
          <div className="trail-achievements-header">
            <h3>Achievement Roadmap</h3>
            <div className="trail-levels-count">{Object.keys(ACHIEVEMENT_LEVELS).length} Levels</div>
          </div>
          
          <div className="trail-achievements-list">
            {Object.entries(ACHIEVEMENT_LEVELS).map(([level, data]) => (
              <div 
                key={level}
                className={`trail-achievement-level-item ${
                  achievementData.currentLevel == level ? 'current' : 
                  achievementData.currentLevel > level ? 'completed' : 'locked'
                }`}
              >
                <div className="trail-level-badge" style={{ backgroundColor: data.color }}>
                  <div className="trail-level-icon">{data.icon}</div>
                </div>
                
                <div className="trail-level-details">
                  <div className="trail-level-name">{data.name}</div>
                  <div className="trail-level-requirement">{data.requirement}</div>
                  <div className="trail-level-volume">Volume: {formatVolume(data.teamVolume)}</div>
                  {data.details && (
                    <div className="trail-level-description">{data.details}</div>
                  )}
                </div>
                
                <div className="trail-level-status">
                  {achievementData.currentLevel == level && <span className="trail-current-indicator">CURRENT</span>}
                  {achievementData.currentLevel > level && <span className="trail-completed-indicator">✓</span>}
                  {achievementData.currentLevel < level && <span className="trail-locked-indicator">🔒</span>}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Leadership Benefits */}
        <div className="trail-achievements-card">
          <div className="trail-achievements-header">
            <h3>Leadership Journey</h3>
            <div className="trail-leaderboard-refresh" onClick={loadAchievementData}>
              🔄
            </div>
          </div>
          
          <div className="trail-leadership-content">
            <div className="trail-leadership-info">
              <h4>Build & Lead Your Team</h4>
              <p>Each level requires you to help others achieve the previous rank, creating a strong leadership structure.</p>
              
              <div className="trail-leadership-steps">
                <div className="trail-step">
                  <div className="trail-step-number">1</div>
                  <div className="trail-step-content">
                    <h5>Start with Cluster</h5>
                    <p>Get 3 direct referrals and build $3,000 in team volume</p>
                  </div>
                </div>
                
                <div className="trail-step">
                  <div className="trail-step-number">2</div>
                  <div className="trail-step-content">
                    <h5>Help Others Succeed</h5>
                    <p>Guide your team members to achieve Cluster rank for Bronze</p>
                  </div>
                </div>
                
                <div className="trail-step">
                  <div className="trail-step-number">3</div>
                  <div className="trail-step-content">
                    <h5>Keep Growing</h5>
                    <p>Continue building volume and developing leaders at each level</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Achievement Benefits */}
      <div className="trail-benefits-card">
        <div className="trail-benefits-header">
          <div className="trail-benefits-icon">🎁</div>
          <div>
            <h3>Achievement Benefits</h3>
            <p>Recognition and status rewards for your leadership achievements</p>
          </div>
        </div>
        
        <div className="trail-benefits-grid">
          <div className="trail-benefit-item">
            <div className="trail-benefit-icon">🏆</div>
            <div className="trail-benefit-content">
              <h4>Leadership Recognition</h4>
              <p>Display your achievement level and gain recognition as a team leader</p>
            </div>
          </div>
          
          <div className="trail-benefit-item">
            <div className="trail-benefit-icon">🌟</div>
            <div className="trail-benefit-content">
              <h4>Elite Status</h4>
              <p>Join the ranks of top performers in the Trail AI ecosystem</p>
            </div>
          </div>
          
          <div className="trail-benefit-item">
            <div className="trail-benefit-icon">📈</div>
            <div className="trail-benefit-content">
              <h4>Team Growth</h4>
              <p>Build a stronger, more successful team through structured progression</p>
            </div>
          </div>
          
          <div className="trail-benefit-item">
            <div className="trail-benefit-icon">🎯</div>
            <div className="trail-benefit-content">
              <h4>Clear Goals</h4>
              <p>Well-defined path for continuous growth and achievement</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RankAchievements;